// Mapas de refracción "Liquid Glass" para un rectángulo redondeado: matemática pura, sin DOM.

/** Aumento de la lupa en el interior del pill (M): se muestrea en c + (p − c) / M. */
export const MAGNIFICATION = 1.2
/** Ancho del bisel curvo en px CSS, donde actúa la refracción de Snell. */
export const BEZEL_WIDTH = 14
/** Desvío máximo de la refracción del canto en px CSS. */
export const REFRACTION_SCALE = 24

export interface GlassMapOptions {
  /** Ancho en px CSS. */
  width: number
  /** Alto en px CSS. */
  height: number
  /** Radio de esquina en px CSS (para un pill: height / 2). */
  radius: number
  /** Ancho del bisel curvo en px CSS (default BEZEL_WIDTH). */
  bezel?: number
  /** Aumento de la lupa (default MAGNIFICATION). */
  magnification?: number
  /** Desvío máximo del canto en px CSS (default REFRACTION_SCALE). */
  refractionScale?: number
  /** Densidad de píxeles del mapa (devicePixelRatio). */
  pixelRatio?: number
  /** Índice de refracción del vidrio (aire = 1). */
  refractiveIndex?: number
}

export interface GlassMaps {
  /** Ancho del mapa en píxeles físicos. */
  width: number
  /** Alto del mapa en píxeles físicos. */
  height: number
  /** Mayor |d| del campo en px CSS: feDisplacementMap usa scale = 2 · maxDisplacement. */
  maxDisplacement: number
  /** RGBA: R/G = desplazamiento X/Y normalizado centrado en 128. */
  displacement: Uint8ClampedArray
  /** RGBA blanco con alfa = intensidad del brillo del canto. */
  specular: Uint8ClampedArray
}

const PROFILE_SAMPLES = 256
// Grosor mínimo del vidrio en el canto, relativo al bisel: sin él el borde exacto no desviaría nada.
const BASE_THICKNESS = 0.35
const LIGHT_X = -Math.SQRT1_2
const LIGHT_Y = -Math.SQRT1_2
const SPECULAR_MAX_ALPHA = 0.6

/** Altura de la superficie convexa tipo squircle: y(x) = (1 − (1 − x)⁴)^¼, x ∈ [0, 1]. */
export const squircleHeight = (x: number): number => Math.pow(1 - Math.pow(1 - x, 4), 0.25)

/** Derivada analítica de squircleHeight: (1 − x)³ · (1 − (1 − x)⁴)^(−¾). */
export const squircleSlope = (x: number): number => {
  const inner = 1 - Math.pow(1 - x, 4)
  return inner <= 0 ? Number.POSITIVE_INFINITY : Math.pow(1 - x, 3) * Math.pow(inner, -0.75)
}

/** Desvío lateral de un rayo vertical que cruza el bisel en x, vía Snell (n₁ = 1, n₂ = n). */
export const refractionOffset = (x: number, n: number): number => {
  const theta1 = Math.atan(squircleSlope(x))
  const theta2 = Math.asin(Math.sin(theta1) / n)
  return (BASE_THICKNESS + squircleHeight(x)) * Math.tan(theta1 - theta2)
}

/** Perfil 1D del desvío normalizado a [0, 1], muestreado sobre el bisel. */
export function buildRefractionProfile(n = 1.5, samples = PROFILE_SAMPLES): Float32Array {
  const profile = new Float32Array(samples + 1)
  let max = 0
  for (let i = 0; i <= samples; i++) {
    const value = refractionOffset(i / samples, n)
    profile[i] = value
    if (value > max) max = value
  }
  if (max > 0) for (let i = 0; i <= samples; i++) profile[i] = (profile[i] ?? 0) / max
  return profile
}

interface EdgeSample {
  /** Distancia hacia adentro hasta el borde (negativa = fuera de la forma). */
  depth: number
  /** Normal saliente unitaria. */
  nx: number
  ny: number
}

/** Distancia con signo al borde de un rectángulo redondeado centrado en el origen, más su normal. */
export function roundedRectEdge(px: number, py: number, halfW: number, halfH: number, r: number): EdgeSample {
  const qx = Math.abs(px) - (halfW - r)
  const qy = Math.abs(py) - (halfH - r)
  const sx = px < 0 ? -1 : 1
  const sy = py < 0 ? -1 : 1
  if (qx > 0 && qy > 0) {
    const len = Math.hypot(qx, qy)
    return { depth: r - len, nx: (sx * qx) / len, ny: (sy * qy) / len }
  }
  const depth = r - Math.max(qx, qy)
  return qx > qy ? { depth, nx: sx, ny: 0 } : { depth, nx: 0, ny: sy }
}

const smoothstep = (t: number): number => {
  const c = Math.min(1, Math.max(0, t))
  return c * c * (3 - 2 * c)
}

/** Genera el mapa de desplazamiento (lupa + canto) y el de brillo especular. */
export function generateGlassMaps(options: GlassMapOptions): GlassMaps {
  const ratio = options.pixelRatio ?? 1
  const width = Math.max(1, Math.round(options.width * ratio))
  const height = Math.max(1, Math.round(options.height * ratio))
  const halfW = options.width / 2
  const halfH = options.height / 2
  const radius = Math.min(options.radius, halfW, halfH)
  const bezel = Math.max(1, options.bezel ?? BEZEL_WIDTH)
  const magnify = 1 / (options.magnification ?? MAGNIFICATION) - 1
  const refractionScale = options.refractionScale ?? REFRACTION_SCALE
  const profile = buildRefractionProfile(options.refractiveIndex ?? 1.5)
  const field = new Float32Array(width * height * 2)
  const specular = new Uint8ClampedArray(width * height * 4)
  let maxDisplacement = 0

  for (let row = 0; row < height; row++) {
    const py = (row + 0.5) / ratio - halfH
    for (let col = 0; col < width; col++) {
      const px = (col + 0.5) / ratio - halfW
      const p = row * width + col
      specular.fill(255, p * 4, p * 4 + 3)
      const edge = roundedRectEdge(px, py, halfW, halfH, radius)
      if (edge.depth < 0) continue

      // Lupa: d = (p − c)(1/M − 1), atenuada hacia el canto con falloff elíptico para empalmar con el bisel.
      const ellipse = Math.min(1, Math.hypot(px / halfW, py / halfH))
      const weight = smoothstep(edge.depth / (bezel * 1.5)) * (1 - 0.3 * ellipse * ellipse)
      let dx = px * magnify * weight
      let dy = py * magnify * weight

      if (edge.depth < bezel) {
        const x = edge.depth / bezel
        const bend = (profile[Math.round(x * PROFILE_SAMPLES)] ?? 0) * refractionScale
        // Muestrea hacia adentro (−normal): el canto comprime lo de atrás, como el borde de una botella.
        dx -= edge.nx * bend
        dy -= edge.ny * bend
        const facing = edge.nx * LIGHT_X + edge.ny * LIGHT_Y
        const light = Math.max(0, facing) + 0.45 * Math.max(0, -facing)
        specular[p * 4 + 3] = 255 * SPECULAR_MAX_ALPHA * light * Math.pow(1 - x, 3)
      }
      field[p * 2] = dx
      field[p * 2 + 1] = dy
      maxDisplacement = Math.max(maxDisplacement, Math.abs(dx), Math.abs(dy))
    }
  }

  const displacement = new Uint8ClampedArray(width * height * 4)
  const norm = maxDisplacement > 0 ? 127 / maxDisplacement : 0
  for (let p = 0; p < width * height; p++) {
    displacement[p * 4] = 128 + (field[p * 2] ?? 0) * norm
    displacement[p * 4 + 1] = 128 + (field[p * 2 + 1] ?? 0) * norm
    displacement[p * 4 + 2] = 128
    displacement[p * 4 + 3] = 255
  }
  return { width, height, maxDisplacement, displacement, specular }
}
